import { useQuery } from '@tanstack/react-query'
import { Copy, Pencil, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { formatMoney, MONEY_SCALE, parseHumanAmount } from '@aeris/money'
import {
  formatDocumentNumber,
  renderReceiptDocumentHtml,
  renderText,
  twoCopiesOf,
  type PaperWidth,
} from '@aeris/printing'
import {
  Badge,
  Button,
  CopyableValue,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  QueryState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchCustomerCredit } from '../lib/api/exchanges.ts'
import { fetchDocumentPayments } from '../lib/api/payments.ts'
import { fetchSalesDocument } from '../lib/api/sales.ts'
import { useAuth } from '../lib/auth-context.ts'
import { useBalcaoAnterior } from '../lib/balcao-anterior.ts'
import { formatDate } from '../lib/date-format.ts'
import { paidLinesOf } from '../lib/document-payments.ts'
import { printHtmlDocument } from '../lib/label-print.ts'
import { voucherOnReceipt } from '../lib/receipt-voucher.ts'
import { salesDocumentToPrint } from '../lib/sales-document-print.ts'
import { documentTitle, STOCK_STATE_LABELS } from '../lib/sales-labels.ts'
import { documentStatusMeta, STATUS_META } from '../lib/sales-status-meta.tsx'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { CorrectPaymentDialog } from './correct-payment-dialog.tsx'
import { SaleFiscalPanel } from './sale-fiscal-panel.tsx'
import { SalesChecksPanel } from './sales-checks-panel.tsx'
import { SalesDocumentHistory } from './sales-document-history.tsx'
import { DocumentStatusBadge } from './status-badge.tsx'

/**
 * Ver o documento inteiro, e reimprimir o papel (F150).
 *
 * "Nessa de editar orçamento / nota, também tem que ter o botão pra ver em
 * detalhes, de imprimir, e imprimir já com 2 vias de uma vez."
 *
 * Antes disto a única forma de ver um documento por inteiro era abrir o
 * FORMULÁRIO DE EDIÇÃO — quem só queria conferir entrava por uma porta onde
 * qualquer tecla muda a venda. Aqui nada é editável.
 *
 * As duas vias saem num papel só, com corte no meio: mandar imprimir duas vezes
 * abriria o diálogo do navegador duas vezes, e é na segunda que o operador
 * desiste — cliente com a via dele e loja sem a dela.
 */

const brl = (wire: string): string => {
  const amount = parseHumanAmount(wire.replace('.', ','), MONEY_SCALE)
  return amount === null ? wire : formatMoney(amount)
}

/** `2,5` e não `2.500000`. */
const quantity = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

export const SalesDocumentDialog = ({
  documentId,
  onClose,
  onEdit,
}: {
  documentId: string
  onClose: () => void
  /**
   * Abrir o formulário de edição a partir daqui (F151).
   *
   * **Quem decide se pode editar é a TELA, não este diálogo.** Em Vendas a
   * regra é `sales.write`; em Orçamentos é `pos.operate && payment.write`
   * **mais** `editScope !== 'none'`, que o servidor manda em cada registro.
   * Repetir a segunda aqui seria mantê-la em dois lugares e descobrir a
   * divergência no dia em que só um dos dois for corrigido.
   *
   * Ausente, o botão não existe.
   */
  onEdit?: (() => void) | undefined
}): ReactNode => {
  const { allows, hasFeature } = useAuth()
  // F361: os códigos à vista; a 4Play fica como era (`balcao-anterior`).
  const showCodes = !useBalcaoAnterior()
  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const [paper, setPaper] = useState<PaperWidth>(48)

  const query = useQuery({
    queryKey: ['sales-document', documentId],
    queryFn: ({ signal }) => fetchSalesDocument(documentId, signal),
  })

  const document = query.data?.document ?? null
  const isQuote = document?.kind === 'quote'

  /*
   * Os pagamentos, só quando fazem sentido e só para quem pode lê-los.
   *
   * O orçamento não tem pagamento nenhum; quem não tem `payment.read` não vai
   * receber a lista de qualquer jeito. Nos dois casos o papel sai sem o bloco
   * de pagamento, que é a verdade, em vez de um 403 barrando a impressão.
   */
  const payments = useQuery({
    queryKey: ['sales-document-payments', documentId],
    queryFn: ({ signal }) =>
      fetchDocumentPayments(
        {
          documentType: 'sale',
          documentId,
          total: document?.totalAmount ?? '0',
        },
        signal
      ),
    enabled: document !== null && !isQuote && allows('payment.read'),
  })

  /* O vale do cliente no pé da nota (15/10). Sem permissão, a nota sai sem. */
  const customerPartyId = document?.customerPartyId ?? null
  const credit = useQuery({
    queryKey: ['customer-credit', customerPartyId, 'receipt'],
    queryFn: ({ signal }) =>
      fetchCustomerCredit(customerPartyId ?? '', signal, {
        page: 1,
        pageSize: 20,
      }),
    enabled: customerPartyId !== null && !isQuote && allows('exchange.read'),
  })

  /** As duas origens do dinheiro, numa lista só. Ver `lib/document-payments.ts`. */
  const paymentLines = paidLinesOf(payments.data)

  /*
   * Os recebimentos que se corrigem (F319): entrada viva, sem estorno, que
   * não é vale nem fiado. A forma errada ("era dinheiro, lançou Pix") se
   * corrige com a senha do gerente.
   */
  const [correcting, setCorrecting] = useState<{
    id: string
    paymentMethodId: string
    methodName: string
    amount: string
  } | null>(null)
  const [corrected, setCorrected] = useState<string | null>(null)
  const canCorrect =
    allows('sales.write') &&
    document !== null &&
    document.status !== 'cancelled'
  const correctable = new Map(
    (payments.data?.payments ?? [])
      .filter(
        (payment, _index, all) =>
          payment.direction === 'in' &&
          payment.status !== 'failed' &&
          payment.methodKind !== 'voucher' &&
          payment.methodKind !== 'customer_credit' &&
          !all.some((other) => other.refundsPaymentId === payment.id)
      )
      .map((payment) => [payment.id, payment])
  )

  const printable =
    document === null
      ? null
      : salesDocumentToPrint({
          document,
          payments: payments.data ?? null,
          company,
          template,
          voucher: voucherOnReceipt(credit.data),
        })

  const send = (copies: 1 | 2): void => {
    if (printable === null) return
    const paperDocument = copies === 2 ? twoCopiesOf(printable) : printable
    printHtmlDocument(renderReceiptDocumentHtml(paperDocument, paper)).catch(
      () => {
        window.print()
      }
    )
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        /*
         * O Pedido volta a se chamar Pedido (F159).
         *
         * Era `isQuote ? 'Orçamento' : 'Venda'` — tudo que não fosse orçamento
         * virava "Venda". O dono abriu um pedido entregue, leu "Venda nº 16" e
         * perguntou por que aquela venda não aparecia para troca. Ela não
         * aparecia porque não era uma venda, e a tela é que dizia que era.
         *
         * `documentTitle` já existia em `lib/sales-labels.ts` e não era usada em
         * lugar nenhum: a interpolação à mão é exatamente como o nome errado
         * nasceu.
         */
        title={document === null ? 'Documento' : documentTitle(document)}
        description="Conferir e reimprimir. Nada muda nesta tela: alterar é pelo botão Editar."
        // O número para copiar e mandar ao cliente ou à loja (F248).
        meta={
          document?.number == null ? undefined : (
            <CopyableValue
              label="Nº"
              value={formatDocumentNumber(document.number)}
              what="número"
            />
          )
        }
      >
        <QueryState
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          what="o documento"
          isEmpty={false}
          empty={null}
        >
          {document === null ? null : (
            <div className="flex flex-col gap-4">
              {/*
                A SITUAÇÃO, que esta ficha não mostrava (F156).
                Era a tela onde alguém abre um documento justamente para
                entender em que pé ele está — e a única coisa que faltava era
                exatamente essa. Aqui há espaço, então a explicação vem em
                texto, sem depender de hover.
              */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <DocumentStatusBadge meta={documentStatusMeta(document)} />
                  {document.stockState === 'none' ? null : (
                    <Badge tone="neutral">
                      {STOCK_STATE_LABELS[document.stockState]}
                    </Badge>
                  )}
                </div>
                <p className="text-(length:--text-xs) text-(--text-secondary)">
                  {hasFeature('fiscal')
                    ? documentStatusMeta(document).what.replace(
                        ' Não emite nota fiscal.',
                        ''
                      )
                    : documentStatusMeta(document).what}
                  {STATUS_META[document.status].next === undefined
                    ? null
                    : ` ${STATUS_META[document.status].next}`}
                </p>
              </div>
              {isQuote ? null : <SaleFiscalPanel saleId={document.id} />}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
                <dt className="text-(--text-muted)">Cliente</dt>
                <dd className="text-(--text-primary)">
                  {document.customerName}
                  {!showCodes ||
                  document.customerContact?.code == null ? null : (
                    <span className="text-(--text-muted)">
                      {` · Cód. ${document.customerContact.code}`}
                    </span>
                  )}
                </dd>
                <dt className="text-(--text-muted)">Filial</dt>
                <dd className="text-(--text-primary)">{document.branchName}</dd>
                <dt className="text-(--text-muted)">Feito em</dt>
                <dd className="text-(--text-primary)">
                  {formatDate(document.issuedAt)}
                </dd>
                {document.salespersonName === null ? null : (
                  <>
                    <dt className="text-(--text-muted)">Vendedor</dt>
                    <dd className="text-(--text-primary)">
                      {document.salespersonName}
                    </dd>
                  </>
                )}
                {document.validUntil === null ? null : (
                  <>
                    <dt className="text-(--text-muted)">Vale até</dt>
                    <dd className="text-(--text-primary)">
                      {formatDate(document.validUntil)}
                    </dd>
                  </>
                )}
              </dl>

              <SalesChecksPanel document={document} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Item</TableHeaderCell>
                      <TableHeaderCell numeric>Qtd.</TableHeaderCell>
                      <TableHeaderCell numeric>Unitário</TableHeaderCell>
                      <TableHeaderCell numeric>Total</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {document.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell className="text-(--text-primary)">
                          {line.description}
                          {!showCodes || line.sku == null ? null : (
                            <span className="block text-(length:--text-xs) text-(--text-muted)">
                              Cód. {line.sku}
                            </span>
                          )}
                        </TableCell>
                        <TableCell numeric>
                          {quantity(line.quantity)} {line.unit}
                        </TableCell>
                        <TableCell numeric>{brl(line.unitPrice)}</TableCell>
                        <TableCell numeric>{brl(line.lineTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <dl className="flex flex-col gap-1 text-(length:--text-sm)">
                <div className="flex justify-between">
                  <dt className="text-(--text-muted)">Subtotal</dt>
                  <dd>{brl(document.subtotalAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-(--text-muted)">Desconto</dt>
                  <dd>{brl(document.discountAmount)}</dd>
                </div>
                <div className="flex justify-between font-(--weight-semibold) text-(--text-primary)">
                  <dt>Total</dt>
                  <dd>{brl(document.totalAmount)}</dd>
                </div>
              </dl>

              {/*
                Como foi pago — os DOIS livros (F159).

                A retirada de balcão grava `payment` contra o documento; a
                entrega grava a baixa do título, que é onde a forma que o
                motoboy recebeu aparece. Esta tela lia só o primeiro, e um
                pedido entregue e quitado saía sem nada escrito — foi a
                pergunta do dono.

                E quando não há nenhum dos dois, a tela DIZ isso. Antes o bloco
                simplesmente sumia, e "sumiu" é indistinguível de defeito de
                layout para quem está olhando.
              */}
              {payments.data === undefined ? null : paymentLines.length ===
                0 ? (
                <p className="text-(length:--text-sm) text-(--text-secondary)">
                  {isQuote
                    ? null
                    : 'Sem pagamento registrado até agora. Quando o valor for recebido, a forma aparece aqui.'}
                </p>
              ) : (
                <dl
                  aria-label="Pagamentos"
                  className="flex flex-col gap-1 text-(length:--text-sm)"
                >
                  {paymentLines.map((line) => {
                    const payment = canCorrect
                      ? correctable.get(line.id)
                      : undefined
                    return (
                      <div
                        key={line.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <dt className="text-(--text-muted)">{line.label}</dt>
                        <dd className="flex items-center gap-2">
                          {brl(line.amount)}
                          {payment === undefined ? null : (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Corrigir a forma de ${line.label}`}
                              onClick={() => {
                                setCorrected(null)
                                setCorrecting({
                                  id: payment.id,
                                  paymentMethodId: payment.paymentMethodId,
                                  methodName: payment.methodName,
                                  amount: payment.amount,
                                })
                              }}
                            >
                              Corrigir forma
                            </Button>
                          )}
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              )}

              {corrected === null ? null : (
                <p
                  role="status"
                  className="text-(length:--text-sm) text-(--success)"
                >
                  {corrected}
                </p>
              )}

              <Field label="Papel" className="w-44">
                {(field) => (
                  <Select
                    {...field}
                    value={String(paper)}
                    onChange={(event) => {
                      setPaper(event.target.value === '48' ? 48 : 32)
                    }}
                  >
                    <option value="32">58 mm</option>
                    <option value="48">80 mm</option>
                  </Select>
                )}
              </Field>

              {printable === null ? null : (
                <pre
                  aria-label="Prévia do papel"
                  className="max-h-72 overflow-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-3 font-(--font-mono) text-(length:--text-xs) leading-tight text-(--text-primary)"
                >
                  {renderText(printable, paper)}
                </pre>
              )}

              <details>
                <summary className="cursor-pointer text-(length:--text-sm) text-(--text-secondary)">
                  Histórico do documento
                </summary>
                <div className="mt-3">
                  <SalesDocumentHistory documentId={documentId} />
                </div>
              </details>
            </div>
          )}
        </QueryState>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {onEdit === undefined ? null : (
            <Button
              variant="secondary"
              leadingIcon={<Pencil aria-hidden="true" className="h-4 w-4" />}
              onClick={onEdit}
            >
              Editar
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={printable === null}
            leadingIcon={<Copy aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              send(2)
            }}
          >
            Imprimir 2 vias
          </Button>
          <Button
            variant="primary"
            disabled={printable === null}
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              send(1)
            }}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
      {correcting === null ? null : (
        <CorrectPaymentDialog
          documentId={documentId}
          payment={correcting}
          onClose={() => {
            setCorrecting(null)
          }}
          onDone={(message) => {
            setCorrecting(null)
            setCorrected(message)
          }}
        />
      )}
    </Dialog>
  )
}
